import { getTotalValue, formatPrice } from '@/utils/currency';
import { extractIdFromShopifyStr } from '@/utils/helpers';
import { ShopifyProduct, ShopifyVariant } from '@/lib/shopify';
import { AuthState } from '@/context/AuthContext';
import {
  getLoveveryId,
  setLoveveryIdInLocalStorage,
} from '@/lib/loveveryIdManager';
import { TypeMuxPlaybackEventProperties } from './videoPlayerHelpers';
import { PRODUCT_EVENT_TYPES, trackProductEvent } from './rudderstack';
import { TypeProductFields } from '@/core/generated-types';
import { TypeProduct } from '@/lib/api/productCatalog/types';
import { noticeError } from './newrelicHelpers';

// How can UniquableEvent be value of and object with le_unique_key? or not?
type UniquableEvent = any & { le_unique_key?: string };

declare global {
  interface Window {
    dataLayer: Record<string, any>;
  }
}

function _ensureDataLayerIsInitialized() {
  window.dataLayer = window.dataLayer || [];
}

export const pushToDataLayer = (event: object) => {
  if (typeof window === 'undefined') return;
  _ensureDataLayerIsInitialized();

  window.dataLayer.push(event);
};

interface TrackingData {
  qtyVal: number;
  shopifyProduct: ShopifyProduct;
  variant: ShopifyVariant;
}

const eventMapping = {
  addToCart: {
    ua: 'addToCart',
    ga4: 'add_to_cart',
  },
  removeFromCart: {
    ua: 'removeFromCart',
    ga4: 'remove_from_cart',
  },
  productView: {
    ua: 'productDetailView',
    ga4: 'view_item',
  },
};

// Use leUniquePushToDataLayer the optional le_unique_key event property to
// prevent duplicate events from being pushed with localStorage

export function leUniqueLocalStorageKey(event: UniquableEvent) {
  return `le_unique_${event.le_unique_key}`;
}

export function leUniqueHasPushedEvent(event: UniquableEvent) {
  if (!event.le_unique_key) {
    return false;
  }

  return 'sent' == localStorage.getItem(leUniqueLocalStorageKey(event));
}

export function leUniqueMarkAsPushed(event: UniquableEvent) {
  if (!event.le_unique_key) {
    return;
  }

  localStorage.setItem(leUniqueLocalStorageKey(event), 'sent');
}

export function leUniqueClearAsPushed(event: UniquableEvent) {
  if (!event.le_unique_key) {
    return;
  }

  localStorage.removeItem(leUniqueLocalStorageKey(event));
}

export function leUniquePushToDataLayer(event: UniquableEvent) {
  if (!leUniqueHasPushedEvent(event)) {
    pushToDataLayer(event);
    leUniqueMarkAsPushed(event);
  }
}

export function pushEvent(
  eventName: keyof typeof eventMapping,
  { qtyVal, shopifyProduct, variant }: TrackingData,
) {
  const data = { qtyVal, shopifyProduct, variant };
  const uaEvent = createEventForUA(eventMapping[eventName].ua, data);
  const ga4Event = createEventForGA4(eventMapping[eventName].ga4, data);

  pushToDataLayer(uaEvent);

  // Clear previous ecommerce object before sending to GA4
  pushToDataLayer({ ecommerce: null });
  pushToDataLayer(ga4Event);
}

export function createEventForUA(
  eventName: string,
  { qtyVal, shopifyProduct, variant }: TrackingData,
) {
  return {
    event: eventName,
    ecommerce: {
      currencyCode: variant.price?.currencyCode,
      [eventName === eventMapping.productView.ua ? 'detail' : 'add']: {
        products: [
          {
            name: shopifyProduct.title,
            id: variant.sku,
            quantity: qtyVal,
            price: formatPrice(variant.price?.amount),
            category: shopifyProduct.productType,
            brand: 'Lovevery',
            variant: variant.title,
            productId: extractIdFromShopifyStr(shopifyProduct.id),
            variantId: extractIdFromShopifyStr(variant.id),
          },
        ],
      },
    },
  };
}

export function createEventForGA4(
  eventName: string,
  { qtyVal, shopifyProduct, variant }: TrackingData,
) {
  return {
    event: eventName,
    ecommerce: {
      currency: variant.price?.currencyCode,
      value: getTotalValue(variant.price?.amount, qtyVal),
      items: [
        {
          item_name: shopifyProduct.title,
          item_id: variant.sku,
          affiliation: `Lovevery ${process.env.NEXT_PUBLIC_REGION}`,
          quantity: qtyVal,
          price: formatPrice(variant.price?.amount),
          item_category: shopifyProduct.productType,
          item_brand: 'Lovevery',
          item_variant: variant.title,
          item_list_name: '',
          item_list_id: '',
          index: 0,
          productId: extractIdFromShopifyStr(shopifyProduct.id),
          variantId: extractIdFromShopifyStr(variant.id),
        },
      ],
    },
  };
}

export async function trackLoggedInCustomer(
  user: AuthState,
  leia: string | undefined,
) {
  const { name, family_name, email, loveveryId } = user.attributes;
  // Sets the loveveryId in local storage from the user object
  setLoveveryIdInLocalStorage(loveveryId);

  if (!leia) {
    noticeError('tlic - leia blank for loveveryId', loveveryId);
  }

  pushToDataLayer({
    visitorType: 'logged in',
    authentication_status: /@lovevery.com\s*$/.test(email)
      ? 'authenticated_lovevery_user'
      : 'authenticated_user',
    leia: leia,
    lovevery_id: loveveryId,
    unified_uuid: loveveryId,
    customer_email: email,
    customer_first_name: name,
    customer_last_name: family_name,
  });
}

export async function trackGuestCustomer(leia: string | undefined) {
  const loveveryId = await getLoveveryId();

  if (!leia) {
    console.error('tgc - leia blank for loveveryId', loveveryId);
  }

  pushToDataLayer({
    visitorType: 'guest',
    authentication_status: 'anonymous_user',
    leia: leia,
    lovevery_id: loveveryId,
  });
}

export const ANONYMOUS = 'anonymous';

export enum EVENT_CATEGORIES {
  NAVIGATION = 'Navigation',
  PLAYBACK = 'Playback',
  DOWNLOAD = 'Download',
}

export enum PLAYBACK_EVENT_TYPES {
  STREAM_ON_DEMAND = 'ExternalControlStreamOnDemand',
  PLAY = 'ExternalControlPlay',
  PAUSE = 'ExternalControlPause',
  PROGRESS = 'Progress',
}

export enum NAVIGATION_EVENT_TYPES {
  COURSE_CHAPTER_EXPANDED = 'CourseChapterExpanded',
  COURSE_CHAPTER_COLLAPSED = 'CourseChapterCollapsed',
  TAB_SEEK_FORWARD = 'TabSeekForward',
  TAB_SEEK_BACKWARD = 'TabSeekBackward',
}

export enum DOWNLOAD_EVENT_TYPES {
  PDF_DOWNLOAD = 'PDFResourceDownload',
}

export function capturePlaybackEvent(
  event: Event,
  properties: TypeMuxPlaybackEventProperties,
  handlerFn?: () => void | undefined,
) {
  if (handlerFn) {
    handlerFn();
  }

  const formattedEvent = {
    eventCategory: EVENT_CATEGORIES.PLAYBACK,
    eventType: event.type,
    properties,
  };

  pushToDataLayer(formattedEvent);
}

type PlaybackProgressEventProperties = {
  title: string;
  percentComplete: number;
  head: number;
};

export function capturePlaybackProgressEvent(
  eventType: PLAYBACK_EVENT_TYPES.PROGRESS,
  properties: PlaybackProgressEventProperties,
) {
  const event = {
    eventCategory: EVENT_CATEGORIES.PLAYBACK,
    eventType,
    properties,
  };

  pushToDataLayer(event);
}

type ExternalPlaybackEventProperties = {
  title: string;
  percentComplete: number;
};

export function captureExternalPlaybackEvent(
  eventType:
    | PLAYBACK_EVENT_TYPES.STREAM_ON_DEMAND
    | PLAYBACK_EVENT_TYPES.PLAY
    | PLAYBACK_EVENT_TYPES.PAUSE,
  properties: ExternalPlaybackEventProperties,
) {
  const event = {
    eventCategory: EVENT_CATEGORIES.NAVIGATION,
    eventType,
    properties,
  };

  pushToDataLayer(event);
}

type CourseChapterEventProperties = {
  title: string;
};

export function captureCourseChapterNavigationEvent(
  eventType:
    | NAVIGATION_EVENT_TYPES.COURSE_CHAPTER_COLLAPSED
    | NAVIGATION_EVENT_TYPES.COURSE_CHAPTER_EXPANDED,
  properties: CourseChapterEventProperties,
) {
  const event = {
    eventCategory: EVENT_CATEGORIES.NAVIGATION,
    eventType,
    properties,
  };

  pushToDataLayer(event);
}

type TabNavigationEventProperties = {
  navigatedToTabTitle: string;
  navigatedToTabPosition: number;
  tabsLength: number;
};

export function captureTabNavigationEvent(
  eventType:
    | NAVIGATION_EVENT_TYPES.TAB_SEEK_FORWARD
    | NAVIGATION_EVENT_TYPES.TAB_SEEK_BACKWARD,
  properties: TabNavigationEventProperties,
) {
  const event = {
    eventCategory: EVENT_CATEGORIES.NAVIGATION,
    eventType,
    properties,
  };

  pushToDataLayer(event);
}

type DownloadEventProperties = {
  fileName: string;
};

export function captureDownloadEvent(
  eventType: DOWNLOAD_EVENT_TYPES.PDF_DOWNLOAD,
  properties: DownloadEventProperties,
) {
  const event = {
    eventCategory: EVENT_CATEGORIES.DOWNLOAD,
    eventType,
    properties,
  };

  pushToDataLayer(event);
}

interface ProductEventData {
  shopifyProduct: ShopifyProduct;
  variant: ShopifyVariant;
  quantity: number;
  url: string;
}

interface ProductFieldsEventData extends ProductEventData {
  product: TypeProductFields;
}

export function formatPriceWithDecimals(
  price: string | number | undefined,
): number {
  if (price === undefined) return 0;

  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numericPrice)) return 0;

  // Format to 2 decimal places and return as number
  return Number.parseFloat(numericPrice.toFixed(2));
}

export function captureProductFieldsEvent(
  eventType: PRODUCT_EVENT_TYPES,
  { shopifyProduct, variant, product, quantity, url }: ProductFieldsEventData,
  component?: string,
  pageType?: string,
  priceDisplay?: string | number,
) {
  trackProductEvent(eventType, {
    url,
    productId: shopifyProduct.id,
    sku: variant.sku,
    category: shopifyProduct.productType,
    name: shopifyProduct.title,
    ageRange: product.ageRange || '',
    ageUnits: '',
    price: formatPriceWithDecimals(priceDisplay || variant.price?.amount),
    quantity,
    component,
    pageType,
  });
}

interface TypeProductEventData extends ProductEventData {
  product: TypeProduct;
}

export function captureTypeProductEvent(
  eventType: PRODUCT_EVENT_TYPES,
  { shopifyProduct, variant, product, quantity, url }: TypeProductEventData,
  component?: string,
  pageType?: string,
) {
  trackProductEvent(eventType, {
    url,
    productId: shopifyProduct.id,
    sku: variant.sku,
    category: shopifyProduct.productType,
    name: shopifyProduct.title,
    ageRange: product.display_age_range || '',
    ageUnits: (product.metadata?.data || {}).age_unit || '',
    price: variant.price?.amount || 0,
    quantity,
    component,
    pageType,
  });
}
